import { apiClient } from '../client';
import { 
  LeadTypeAssignment,
  LeadTypeAssignmentRequest,
  UserLeadTypes,
  LeadTypeAssignmentResponse,
  Group,
  GroupCreatePayload,
  UserCoreKVSetting,
  LeadFilterOptions,
  LeadGroupSummary,
  PatchSupportDailyLimitsPayload,
  GeoPartyCatalog,
} from '@/types/userSettings';

export const SUPPORT_DAILY_LIMIT_SELF_TRIAL_KEY = 'SUPPORT_DAILY_LIMIT_SELF_TRIAL';
export const SUPPORT_DAILY_LIMIT_OTHER_KEY = 'SUPPORT_DAILY_LIMIT_OTHER';
export const SUPPORT_RESOLVE_RATE_GOAL_KEY = 'SUPPORT_RESOLVE_RATE_GOAL';


/** Coerce core-KV numeric values (often JSON numbers, sometimes strings). */
function coerceNonNegativeInt(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return Math.floor(n);
  }
  return null;
}

/** Daily fresh-lead cap from TenantMemberSetting core KV key `DAILY_LIMIT`. */
export function resolveDailyFreshLeadLimitFromKv(kv: UserCoreKVSetting[]): number | null {
  const limitRow = kv.find((row) => row.key === 'DAILY_LIMIT');
  return coerceNonNegativeInt(limitRow?.value);
}

/** Assigned lead-group id from TenantMemberSetting core KV key `GROUP`. */
export function resolveGroupIdFromKv(kv: UserCoreKVSetting[]): number | null {
  const groupRow = kv.find((row) => row.key === 'GROUP');
  return coerceNonNegativeInt(groupRow?.value);
}

export function resolveSupportDailyLimitsFromKv(kv: UserCoreKVSetting[]): {
  self_trial: number | null;
  other: number | null;
} {
  const stRow = kv.find((row) => row.key === SUPPORT_DAILY_LIMIT_SELF_TRIAL_KEY);
  const otherRow = kv.find((row) => row.key === SUPPORT_DAILY_LIMIT_OTHER_KEY);
  return {
    self_trial: coerceNonNegativeInt(stRow?.value),
    other: coerceNonNegativeInt(otherRow?.value),
  };
}

export function resolveSupportResolveRateGoalFromKv(kv: UserCoreKVSetting[]): number | null {
  const row = kv.find((row) => row.key === SUPPORT_RESOLVE_RATE_GOAL_KEY);
  return coerceNonNegativeInt(row?.value);
}

// Lead Type Assignment API functions
export const leadTypeAssignmentApi = {
  // Get all lead type assignments for the tenant
  async getAll(rmsEndpoint?: string): Promise<LeadTypeAssignment[]> {
    try {
      // Use the lead-type-assignments endpoint directly (returns TenantMembership-based data)
      const assignmentsResponse = await apiClient.get('/user-settings/lead-type-assignments/');
      const assignmentsData = assignmentsResponse.data;
      
      // The endpoint returns an array of assignments with TenantMembership IDs
      if (Array.isArray(assignmentsData)) {
        return assignmentsData.map((assignment: any) => ({
          user_id: String(assignment.user_id || assignment.tenant_membership_id), // Use TenantMembership ID
          user_name: assignment.user_name || 'Unknown',
          user_email: assignment.user_email || '',
          tenant_membership_id: assignment.tenant_membership_id || assignment.user_id,
          lead_types: assignment.lead_types || [],
          lead_sources: assignment.lead_sources || [],
          lead_statuses: assignment.lead_statuses || [],
          daily_target: assignment.daily_target,
          daily_limit: assignment.daily_limit,
          assigned_leads_count: assignment.assigned_leads_count || 0
        }));
      }
      
      return [];
    } catch (error: any) {
      if (error.response?.status === 403) {

        throw new Error('Access denied: insufficient permissions to access lead type assignments');

      }
      // For 404, return empty array instead of throwing (no users found is not an error)
      if (error.response?.status === 404) {
        console.log('No assignments found (404), returning empty array');
        return [];
      }
      console.error('Error fetching lead type assignments:', error);
      throw error;
    }
  },

  // Assign lead types to a user
  async assign(data: LeadTypeAssignmentRequest, endpoint?: string): Promise<LeadTypeAssignmentResponse> {
    console.log('Saving lead type assignment via API...');
    
    try {
      const assignmentsEndpoint = endpoint || '/user-settings/lead-type-assignments/';

      const response = await apiClient.post(assignmentsEndpoint, {
        user_id: data.user_id,
        lead_types: data.lead_types,
        lead_sources: data.lead_sources ?? [],
        lead_statuses: data.lead_statuses ?? [],
        daily_target: data.daily_target,
        daily_limit: data.daily_limit,
      });

      const result = response.data;
      console.log('Lead type assignment saved successfully:', result);

      return {
        user_id: result.user_id || data.user_id,
        user_name: result.user_name || 'Unknown',
        user_email: result.user_email || '',
        lead_types: result.lead_types || data.lead_types,
        lead_sources: result.lead_sources ?? data.lead_sources ?? [],
        lead_statuses: result.lead_statuses ?? data.lead_statuses ?? [],
        daily_target: result.daily_target,
        daily_limit: result.daily_limit,
        created: result.created || false
      };
    } catch (error: any) {
      console.error('Error in assign:', error);
      throw error;
    }
  },

  // Get lead types assigned to a specific user
  async getUserLeadTypes(userId: string): Promise<UserLeadTypes> {
    try {
      const response = await apiClient.get(`/user-settings/users/${userId}/lead-types/`);
      const result = response.data;
      
      return {
        user_id: result.user_id || userId,
        lead_types: result.lead_types || []
      };
    } catch (error: any) {
      // If 404, user has no lead types assigned
      if (error.response?.status === 404) {
        return {
          user_id: userId,
          lead_types: []
        };
      }
      console.error('Error in getUserLeadTypes:', error);
      throw error;
    }
  },

  // All lead filter dropdown values in one request (faster than 4 separate calls).
  async getLeadFilterOptions(endpoint?: string): Promise<LeadFilterOptions> {
    const empty: LeadFilterOptions = {
      lead_types: [],
      lead_sources: [],
      lead_statuses: [],
      lead_states: [],
    };

    try {
      const filterOptionsEndpoint = endpoint || '/user-settings/lead-filter-options/';
      const response = await apiClient.get(filterOptionsEndpoint);
      const responseData = response.data;

      return {
        lead_types: Array.isArray(responseData.lead_types) ? responseData.lead_types : [],
        lead_sources: Array.isArray(responseData.lead_sources) ? responseData.lead_sources : [],
        lead_statuses: Array.isArray(responseData.lead_statuses) ? responseData.lead_statuses : [],
        lead_states: Array.isArray(responseData.lead_states) ? responseData.lead_states : [],
      };
    } catch (error: any) {
      console.error('Error fetching lead filter options:', error);
      return empty;
    }
  },

  // Get available lead types from records' affiliated_party field
  async getAvailableLeadTypes(endpoint?: string): Promise<string[]> {
    try {
      // Use the dedicated lead types endpoint if no custom endpoint is provided
      const leadTypesEndpoint = endpoint || '/user-settings/lead-types/';
      
      const response = await apiClient.get(leadTypesEndpoint);
      const responseData = response.data;
      
      // The endpoint returns { "lead_types": [...] }
      if (responseData.lead_types && Array.isArray(responseData.lead_types)) {
        return responseData.lead_types;
      }
      
      // Fallback: return empty array if format is unexpected
      return [];
    } catch (error: any) {
      console.error('Error fetching available lead types:', error);
      // Return empty array on error
      return [];
    }
  },

  // Get available lead sources from records' lead_source field
  async getAvailableLeadSources(endpoint?: string): Promise<string[]> {
    try {
      const leadSourcesEndpoint = endpoint || '/user-settings/lead-sources/';
      const response = await apiClient.get(leadSourcesEndpoint);
      const responseData = response.data;
      if (responseData.lead_sources && Array.isArray(responseData.lead_sources)) {
        return responseData.lead_sources;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching available lead sources:', error);
      return [];
    }
  },

  // Get available lead statuses from records' lead_stage field
  async getAvailableLeadStatuses(endpoint?: string): Promise<string[]> {
    try {
      const leadStatusesEndpoint = endpoint || '/user-settings/lead-statuses/';
      const response = await apiClient.get(leadStatusesEndpoint);
      const responseData = response.data;
      if (responseData.lead_statuses && Array.isArray(responseData.lead_statuses)) {
        return responseData.lead_statuses;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching available lead statuses:', error);
      return [];
    }
  },

  // Get available lead states from records' state field
  async getAvailableLeadStates(endpoint?: string): Promise<string[]> {
    try {
      const leadStatesEndpoint = endpoint || '/user-settings/lead-states/';
      const response = await apiClient.get(leadStatesEndpoint);
      const responseData = response.data;
      if (responseData.lead_states && Array.isArray(responseData.lead_states)) {
        return responseData.lead_states;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching available lead states:', error);
      return [];
    }
  },

  /** Circle state/district/party catalog for User Management dropdowns (Name (id)). */
  async getGeoPartyCatalog(stateId?: string): Promise<GeoPartyCatalog> {
    const empty: GeoPartyCatalog = { states: [], districts: [], parties: [] };
    try {
      const response = await apiClient.get('/user-settings/geo-party-catalog/', {
        params: stateId ? { state_id: stateId } : undefined,
      });
      const data = response.data || {};
      const toNum = (v: unknown): number | null => {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
        return null;
      };
      const normalize = (rows: unknown[]): GeoPartyCatalog['states'] =>
        (Array.isArray(rows) ? rows : [])
          .map((row: any) => {
            const value = toNum(row?.value);
            if (value == null) return null;
            const stateId = toNum(row?.state_id);
            return {
              value,
              label: typeof row?.label === 'string' ? row.label : String(value),
              name: typeof row?.name === 'string' ? row.name : String(value),
              ...(stateId != null ? { state_id: stateId } : {}),
            };
          })
          .filter(Boolean) as GeoPartyCatalog['states'];
      return {
        version: data.version,
        states: normalize(data.states),
        districts: normalize(data.districts),
        parties: normalize(data.parties),
      };
    } catch (error: any) {
      console.error(
        'Error fetching geo-party catalog:',
        error?.response?.status,
        error?.response?.data || error?.message || error
      );
      return empty;
    }
  },

  // Get supported queue types
  async getAvailableQueueTypes(endpoint?: string): Promise<string[]> {
    try {
      const queueTypesEndpoint = endpoint || '/user-settings/queue-types/';
      const response = await apiClient.get(queueTypesEndpoint);
      const responseData = response.data;
      if (responseData.queue_types && Array.isArray(responseData.queue_types)) {
        return responseData.queue_types;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching available queue types:', error);
      return [];
    }
  },

  async getUserCoreKVSettings(userId: string): Promise<UserCoreKVSetting[]> {
    try {
      const response = await apiClient.get(`/user-settings/users/${userId}/core-kv-settings/`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (error.response?.status === 404) return [];
      console.error('Error fetching user core kv settings:', error);
      return [];
    }
  },

  async patchSupportDailyLimits(
    userId: string,
    data: PatchSupportDailyLimitsPayload
  ): Promise<UserCoreKVSetting[]> {
    const response = await apiClient.patch(
      `/user-settings/users/${userId}/core-kv-settings/`,
      data
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  /** Patch arbitrary core-KV keys (used for tenant custom User Management fields). */
  async patchCoreKvSettings(
    userId: string,
    data: Record<string, string | number | boolean | null>
  ): Promise<UserCoreKVSetting[]> {
    const response = await apiClient.patch(
      `/user-settings/users/${userId}/core-kv-settings/`,
      data
    );
    return Array.isArray(response.data) ? response.data : [];
  },
};

export const groupsApi = {
  async getAll(): Promise<Group[]> {
    const response = await apiClient.get('/user-settings/groups/');
    return response.data;
  },

  async getById(id: number): Promise<Group> {
    const response = await apiClient.get(`/user-settings/groups/${id}/`);
    return response.data;
  },

  /** Current user's assigned group + fresh leads pool (for RM pending page). */
  async getMyLeadGroupSummary(): Promise<LeadGroupSummary> {
    const response = await apiClient.get('/user-settings/me/lead-group-summary/');
    return response.data;
  },

  async create(payload: GroupCreatePayload): Promise<Group> {
    const response = await apiClient.post('/user-settings/groups/', payload);
    return response.data;
  },

  async update(id: number, payload: GroupCreatePayload): Promise<Group> {
    const response = await apiClient.put(`/user-settings/groups/${id}/`, payload);
    return response.data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`/user-settings/groups/${id}/`);
  },
};

